import { ExpandableSection } from 'src/modules/shared/components/molecules/expandable-section';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { ConversationFieldsSection } from '../../molecules/contacts/contact-detail-panel/conversation-fields-section';
import { type FunnelConversation } from 'src/api/generated';
import { Text } from 'src/modules/shared/components/atoms/text';
import { formatDate } from 'src/modules/shared/utils/formatting/date.util';
import { PiCaretLeft, PiCaretRight, PiTableLight } from 'react-icons/pi';
import { useNavigate } from '@tanstack/react-router';
import { Route as SettingsCustomFieldsRoute } from 'src/routes/settings/custom-fields';
import { settingsSearchSchema } from 'src/modules/settings/schemas/settings.schema';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { CrmCustomFieldPermission } from 'src/modules/shared/types/permissions.type';
import { useMediaQuery } from 'src/modules/shared/hooks/use-media-query';
import { COMMUNICATIONS_SMALL_SCREEN_MEDIA_QUERY } from 'src/modules/communications/utils/view-preference.util';

interface ConversationsSectionProps {
  conversations: FunnelConversation[];
  loading: boolean;
  error: Error | null;
  selectedConversationIndex: number;
  onPreviousConversation: () => void;
  onNextConversation: () => void;
}

export const ConversationsSection: React.FC<ConversationsSectionProps> = ({
  conversations,
  loading,
  error,
  selectedConversationIndex,
  onPreviousConversation,
  onNextConversation,
}) => {
  const { t } = useI18n();
  const titleClassName = 'text-xs sm:text-xs uppercase text-secondary-500';
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(COMMUNICATIONS_SMALL_SCREEN_MEDIA_QUERY);
  const { hasPermission: canCreateCustomFields, loading: loadingPermissions } =
    useHasPermissions(CrmCustomFieldPermission.Create);
  const { hasPermission: canUpdateCustomFields } = useHasPermissions(
    CrmCustomFieldPermission.Update
  );
  const canEditCustomFields = canCreateCustomFields || canUpdateCustomFields;
  const sectionTitle = t('communications.contactPanel.conversations.title');

  if (loading) {
    return (
      <ExpandableSection
        title={sectionTitle}
        titleClassName={titleClassName}
        className="bg-neutral-50/40"
        contentClassName="max-h-none min-h-[4rem]"
        compactHeaderOnMobile
      >
        <ConversationFieldsSection loading />
      </ExpandableSection>
    );
  }

  if (error) {
    return (
      <ExpandableSection title={sectionTitle} titleClassName={titleClassName}>
        <ErrorState
          title={t('communications.contactPanel.conversations.error.title')}
          description={t(
            'communications.contactPanel.conversations.error.description'
          )}
        />
      </ExpandableSection>
    );
  }

  if (conversations.length === 0) {
    return (
      <ExpandableSection title={sectionTitle} titleClassName={titleClassName}>
        <EmptyState
          description={t('communications.contactPanel.conversations.empty')}
        />
      </ExpandableSection>
    );
  }

  const selectedConversation = conversations[selectedConversationIndex];
  const selectedConversationFunnelId =
    typeof selectedConversation.funnelId === 'number'
      ? selectedConversation.funnelId
      : undefined;

  return (
    <ExpandableSection
      title={sectionTitle}
      titleClassName={titleClassName}
      className="bg-neutral-50/40"
      contentClassName="min-h-[4rem]"
      compactHeaderOnMobile
      actions={
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex flex-col gap-0 min-w-0 items-center">
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <Button
                variant="neutral"
                appearance="ghost"
                size="xs"
                onClick={() => {
                  onPreviousConversation();
                }}
                isIconOnly
                className="flex-shrink-0"
              >
                <PiCaretLeft className="h-4 w-4" />
              </Button>

              <Text
                size="tiny"
                color="secondary"
                className="flex-shrink-0 whitespace-nowrap"
              >
                {selectedConversationIndex + 1} / {conversations.length}
              </Text>

              <Button
                variant="neutral"
                appearance="ghost"
                size="xs"
                onClick={() => {
                  onNextConversation();
                }}
                isIconOnly
                className="flex-shrink-0"
              >
                <PiCaretRight className="h-4 w-4" />
              </Button>
            </div>

            <Text
              size="tiny"
              color="secondary"
              className="w-full px-2 text-right flex-shrink-0 whitespace-nowrap"
              weight="medium"
            >
              {formatDate(selectedConversation.createdAt, 'DD-MM-YYYY')}
            </Text>
          </div>

          {!loadingPermissions && canEditCustomFields && !isSmallScreen && (
            <Tooltip
              enabled={!!selectedConversationFunnelId}
              content={t(
                'communications.contactPanel.conversations.customFields.create'
              )}
              placement="bottom"
              size="sm"
            >
              <Button
                variant="neutral"
                appearance="ghost"
                size="sm"
                isIconOnly
                disabled={!selectedConversationFunnelId}
                onClick={() => {
                  if (!selectedConversationFunnelId) return;

                  void navigate({
                    to: SettingsCustomFieldsRoute.to,
                    search: () =>
                      settingsSearchSchema.parse({
                        subtab: 'custom-fields',
                        scope: 'funnel',
                        funnelId: selectedConversationFunnelId,
                        view: 'create',
                        entity: 'FUNNEL_CONVERSATION',
                      }),
                  });
                }}
              >
                <PiTableLight className="h-4 w-4" />
              </Button>
            </Tooltip>
          )}
        </div>
      }
    >
      <ConversationFieldsSection conversation={selectedConversation} />
    </ExpandableSection>
  );
};
